import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Handshake, Rocket, Cpu, Users } from 'lucide-react';

const pillars = [
  {
    icon: Handshake,
    title: 'Africa–India Business Bridge',
    description: 'Cross-border trade, partnerships, market entry strategies across 11 countries.',
    color: '#D4AF37'
  },
  {
    icon: Rocket,
    title: 'Starting & Scaling Businesses',
    description: 'From idea validation to execution — especially in emerging markets.',
    color: '#007AFF'
  },
  {
    icon: Cpu,
    title: 'Technology for Development',
    description: 'ICT, fintech, rural connectivity, digital infrastructure for African markets.',
    color: '#50C878'
  },
  {
    icon: Users,
    title: 'Entrepreneurship & Empowerment',
    description: 'Mentoring youth, women, and first-time founders to build lasting enterprises.',
    color: '#9B59B6'
  },
];

export const ExpertisePillars = () => {
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const scrollToPackages = () => {
    const element = document.querySelector('#packages');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="expertise" 
      className="section bg-[#080808] relative overflow-hidden"
      data-testid="expertise-section"
    >
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#D4AF37] opacity-[0.02] rounded-full blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">What I Work On</p>
          <h2 className="section-title">
            4 Expertise <span className="gradient-text">Pillars</span>
          </h2>
        </motion.div>

        {/* Four Pillars Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {pillars.map((pillar, index) => (
            <motion.div
              key={pillar.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.15 }}
              className="glass-light rounded-2xl p-6 card-hover text-center"
              data-testid={`pillar-${pillar.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <motion.div
                className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                whileHover={{ scale: 1.1 }}
                style={{ backgroundColor: `${pillar.color}15` }}
              >
                <pillar.icon size={28} style={{ color: pillar.color }} />
              </motion.div>
              <h3 className="text-lg font-semibold text-white mb-3">{pillar.title}</h3>
              <p className="text-sm text-white/50 leading-relaxed">{pillar.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Transition Line */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <p className="text-lg text-white/70 mb-6">
            If any of this resonates with where you are — or where you want to go — here's how we can work together.
          </p>
          <button
            onClick={scrollToPackages}
            className="btn-secondary"
            data-testid="explore-packages-btn"
          >
            Explore My Packages
          </button>
        </motion.div>
      </div>
    </section>
  );
};
